import React from "react";

// Product tax treatment (Oct 2026). One product is taxed in exactly one of
// three ways, enforced by the backend at checkout for every payment rail
// (`services/commerce.py::resolve_line_tax_rate`):
//
//   default  — use the catalog's default rate (Commerce -> Taxes)
//   exempt   — no tax at all (`tax_exempt: true`)
//   custom   — this product's own rate (`tax_rate_override`, a decimal;
//              0 is a deliberate 0% rate, distinct from "default")
//
// Saved through `PATCH /commerce/products/{id}/tax`, separately from the
// generic product PATCH — the same "system-adjacent concern, separate
// endpoint" pattern the parcel and Clover sections follow.

export const TAX_TREATMENT_DEFAULT = "default";
export const TAX_TREATMENT_EXEMPT = "exempt";
export const TAX_TREATMENT_CUSTOM = "custom";

// The backend stores 0–1; the editor shows 0–100 percent.
export const MAX_TAX_RATE_PERCENT = 100;

function formatPercent(decimal) {
  // 0.0825 -> "8.25"; keeps up to 4 decimal places, drops trailing zeros.
  return String(Number((Number(decimal) * 100).toFixed(4)));
}

function hasOverride(product) {
  const v = product?.tax_rate_override;
  return v !== null && v !== undefined && v !== "";
}

/** The editor state for a product as stored. Exempt wins over a stored
 *  custom rate, exactly as the backend's precedence does. */
export function taxTreatmentFromProduct(product) {
  if (product?.tax_exempt === true) return { mode: TAX_TREATMENT_EXEMPT, ratePercent: "" };
  if (hasOverride(product)) {
    return { mode: TAX_TREATMENT_CUSTOM, ratePercent: formatPercent(product.tax_rate_override) };
  }
  return { mode: TAX_TREATMENT_DEFAULT, ratePercent: "" };
}

/** Parse the percentage field. Returns { decimal } or { error }. */
export function parseTaxRatePercent(raw) {
  const text = String(raw ?? "").trim();
  if (text === "") return { error: "Enter a tax rate, or choose another option." };
  if (!/^\d+(\.\d+)?$/.test(text)) return { error: "Enter the rate as a number, e.g. 8.25." };
  const pct = Number(text);
  if (!Number.isFinite(pct)) return { error: "Enter the rate as a number, e.g. 8.25." };
  if (pct < 0 || pct > MAX_TAX_RATE_PERCENT) {
    return { error: `The rate must be between 0 and ${MAX_TAX_RATE_PERCENT}%.` };
  }
  // 6 decimal places of the decimal rate = 4 of the percentage.
  return { decimal: Number((pct / 100).toFixed(6)) };
}

/** The exact PATCH body for the chosen treatment, or { error }. */
export function taxTreatmentPayload(state) {
  if (state.mode === TAX_TREATMENT_EXEMPT) return { body: { tax_exempt: true, tax_rate_override: null } };
  if (state.mode === TAX_TREATMENT_CUSTOM) {
    const parsed = parseTaxRatePercent(state.ratePercent);
    if (parsed.error) return { error: parsed.error };
    return { body: { tax_exempt: false, tax_rate_override: parsed.decimal } };
  }
  return { body: { tax_exempt: false, tax_rate_override: null } };
}

/** True when saving `state` would change what is stored for `product`. */
export function taxTreatmentChanged(state, product) {
  const persisted = taxTreatmentFromProduct(product);
  if (state.mode !== persisted.mode) return true;
  if (state.mode !== TAX_TREATMENT_CUSTOM) return false;
  const next = parseTaxRatePercent(state.ratePercent);
  if (next.error) return true; // let validation report it
  return next.decimal !== Number(Number(product?.tax_rate_override).toFixed(6));
}

export function taxTreatmentLabel(state) {
  if (state.mode === TAX_TREATMENT_EXEMPT) return "Tax exempt";
  if (state.mode === TAX_TREATMENT_CUSTOM) return `Custom tax rate ${state.ratePercent}%`;
  return "Use catalog default";
}

export default function ProductTaxTreatmentFields({ value, onChange, catalogDefaultRate, error }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const defaultLabel = Number.isFinite(Number(catalogDefaultRate)) && catalogDefaultRate !== null && catalogDefaultRate !== undefined
    ? `${formatPercent(catalogDefaultRate)}%`
    : null;
  const option = (mode, label, hint) => (
    <label className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer">
      <input
        type="radio"
        name="product-tax-treatment"
        value={mode}
        checked={value.mode === mode}
        onChange={() => set({ mode })}
        data-testid={`product-tax-treatment-${mode}`}
        className="mt-1 h-4 w-4 accent-slate-900"
      />
      <span>
        <span className="font-medium">{label}</span>
        {hint && <span className="block text-[11px] text-slate-500">{hint}</span>}
      </span>
    </label>
  );
  return (
    <div className="pt-3 border-t border-slate-200" data-testid="product-tax-treatment-section">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Tax treatment</div>
      <div className="space-y-2" role="radiogroup" aria-label="Tax treatment">
        {option(
          TAX_TREATMENT_DEFAULT,
          "Use catalog default",
          defaultLabel
            ? `Taxed at this catalog's default rate (${defaultLabel}), set in Commerce → Taxes.`
            : "Taxed at this catalog's default rate, set in Commerce → Taxes.",
        )}
        {option(TAX_TREATMENT_EXEMPT, "Tax exempt", "No tax is charged on this product. Discounts still apply.")}
        {option(TAX_TREATMENT_CUSTOM, "Custom tax rate (%)", "This product's own rate instead of the catalog default. 0% is allowed and means this product is taxed at zero.")}
        {value.mode === TAX_TREATMENT_CUSTOM && (
          <div className="pl-6">
            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="decimal"
                value={value.ratePercent}
                onChange={(e) => set({ ratePercent: e.target.value })}
                placeholder="e.g. 8.25"
                aria-label="Custom tax rate percent"
                data-testid="product-tax-rate-input"
                className="w-28 text-sm border border-slate-300 rounded-md px-2 py-1.5"
              />
              <span className="text-sm text-slate-600">%</span>
            </div>
            {error && (
              <div className="text-[11px] text-rose-600 mt-1" data-testid="product-tax-rate-error">{error}</div>
            )}
          </div>
        )}
      </div>
      <div className="text-[11px] text-slate-500 mt-2">
        Applies to orders placed after you save. Orders already placed keep the tax they were charged.
      </div>
    </div>
  );
}
